import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Label,
  TextInput,
  Button,
  useThemeMode,
} from "flowbite-react";
import Select from "react-select";
import { HiSearch } from "react-icons/hi";
import { useLazyGetAgentHistoricalOccupancyReportQuery } from "../../api/reportsApi/reports";
import { useReportCompanyParam } from "../../hooks/useReportCompanyParam";
import DateRangePresets from "../../components/reports/DateRangePresets";
import { useGetAgentsQuery } from "../../api/agentApi/agent";
import { getErrorMessage } from "../../utils/requestUtils";
import { toast } from "@/lib/toast";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

const AgentHistoricalOccupancyReport: FC = () => {
  const { t } = useTranslation(["reports"]);
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const today = new Date().toISOString().split("T")[0];

  const [selectedAgent, setSelectedAgent] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [triggerReport, { data: reportData, isLoading }] =
    useLazyGetAgentHistoricalOccupancyReportQuery();
  const companyParam = useReportCompanyParam();
  const { data: agentsData, isLoading: isAgentsLoading } = useGetAgentsQuery(
    {},
  );

  const agentOptions =
    agentsData?.items?.map((agent: any) => ({
      label: agent.display_name || agent.agent_username,
      value: String(agent.agent_id),
    })) || [];

  const handleSearch = async () => {
    if (!selectedAgent || !startDate || !endDate) {
      toast.warn(t("selectClientDateWarning"));
      return;
    }

    if (startDate > endDate) {
      toast.error(t("startDateHigherThanEndDate"));
      return;
    }

    try {
      await triggerReport({
        live_agent_id: Number(selectedAgent.value),
        start_date: startDate,
        end_date: endDate,
        ...companyParam,
      }).unwrap();
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    }
  };

  const chartSeries = [
    {
      name: t("occupancyRate"),
      data: reportData?.daily_stats.map((stat) => stat.occupancy_rate) || [],
    },
  ];

  const chartOptions: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      background: "transparent",
      toolbar: {
        show: true,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 5,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories:
        reportData?.daily_stats.map((stat) => stat.formatted_date) || [],
      labels: {
        style: {
          colors: isDark ? "#fff" : "#000",
        },
      },
    },
    yaxis: {
      title: {
        text: t("occupancyRate"),
        style: {
          color: isDark ? "#fff" : "#000",
        },
      },
      labels: {
        style: {
          colors: isDark ? "#fff" : "#000",
        },
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val + "";
        },
      },
      theme: isDark ? "dark" : "light",
    },
    theme: {
      mode: isDark ? "dark" : "light",
    },
    colors: ["#1A56DB"],
  };

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: isDark ? "#374151" : "#f9fafb",
      borderColor: isDark ? "#4b5563" : "#d1d5db",
      color: isDark ? "white" : "black",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: isDark ? "white" : "black",
    }),
    input: (provided: any) => ({
      ...provided,
      color: isDark ? "white" : "black",
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: isDark ? "#374151" : "white",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? isDark
          ? "#1f2937"
          : "#e5e7eb"
        : state.isFocused
          ? isDark
            ? "#4b5563"
            : "#f3f4f6"
          : isDark
            ? "#374151"
            : "white",
      color: isDark ? "white" : "black",
    }),
  };

  return (
    <div className="px-4 pt-6">
      <div className="flex flex-col gap-4">
        <Card>
          <DateRangePresets
            className="mb-4"
            startDate={startDate}
            endDate={endDate}
            onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
          />
          <div className="flex flex-col items-end gap-4 md:flex-row">
            <div className="w-full md:w-1/3">
              <div className="mb-2 block">
                <Label
                  htmlFor="agent-select"
                  value={t("selectAgent")}
                />
              </div>
              <Select
                id="agent-select"
                options={agentOptions}
                value={selectedAgent}
                onChange={setSelectedAgent}
                isLoading={isAgentsLoading}
                placeholder={t("selectAgentPlaceholder")}
                styles={customSelectStyles}
              />
            </div>

            <div className="w-full md:w-1/4">
              <div className="mb-2 block">
                <Label htmlFor="start-date" value={t("startDate")} />
              </div>
              <TextInput
                id="start-date"
                type="date"
                value={startDate}
                max={today}
                onChange={(e) => setStartDate(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key !== "Tab") e.preventDefault();
                }}
                required
              />
            </div>

            <div className="w-full md:w-1/4">
              <div className="mb-2 block">
                <Label htmlFor="end-date" value={t("endDate")} />
              </div>
              <TextInput
                id="end-date"
                type="date"
                value={endDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key !== "Tab") e.preventDefault();
                }}
                required
              />
            </div>

            <Button
              onClick={handleSearch}
              disabled={isLoading}
              isProcessing={isLoading}
              color="primary"
            >
              <HiSearch className="mr-2 h-5 w-5" />
              {t("search")}
            </Button>
          </div>
        </Card>

        {reportData && (
          <Card>
            <h5 className="mb-4 text-xl font-bold leading-none text-gray-900 dark:text-white">
              {t(
                "reports:historicalOccupancyTitle",
              )}
            </h5>
            <div id="chart">
              <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={350}
              />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AgentHistoricalOccupancyReport;
